import { useSyncExternalStore } from 'react';

/**
 * Shared module-level second-tick. A single rAF loop drives all subscribers,
 * so every consumer (`<RestCountdown>`, `<ElapsedTime>`, etc.) re-renders in
 * the same React batch and reads `Date.now()` within the same commit —
 * guaranteeing all displayed countdown/elapsed values stay in lockstep.
 *
 * Components that subscribe to this tick re-render once per second; their
 * parents do NOT — so the surrounding tree (e.g. ActiveWorkout) is unaffected.
 */
const listeners = new Set<() => void>();
let rafId = 0;
let lastSecond = -1;
let snapshot = 0;

function loop() {
  const sec = Math.floor(Date.now() / 1000);
  if (sec !== lastSecond) {
    lastSecond = sec;
    snapshot = sec;
    listeners.forEach((l) => l());
  }
  rafId = requestAnimationFrame(loop);
}

function subscribe(cb: () => void) {
  listeners.add(cb);
  if (listeners.size === 1) {
    lastSecond = -1;
    rafId = requestAnimationFrame(loop);
  }
  return () => {
    listeners.delete(cb);
    if (listeners.size === 0) {
      cancelAnimationFrame(rafId);
    }
  };
}

function getSnapshot() {
  return snapshot;
}

function getServerSnapshot() {
  return 0;
}

export function useSecondTick() {
  return useSyncExternalStore(subscribe, getSnapshot, getServerSnapshot);
}
